"use client";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "components/shadcn/ui/accordion";
import { Button } from "components/shadcn/ui/button";
import { Input } from "components/shadcn/ui/input";
import { Search } from "lucide-react";
import {useContext, useEffect, useMemo, useState} from "react";
import classNames from "classnames";
import {Dictionary} from "@/types/hygiena-types";
import {DictionaryContext} from "@/context/dictionary-context";

export interface FAQ {
	question: string;
	answer: string;
}

export interface Subsection {
	id: string;
	title: string;
	faqs: FAQ[];
}

export interface Section {
	id: string;
	title: string;
	subsections: Subsection[];
}

export interface FAQData {
	sections: Section[];
}

export interface FAQSectionProps {
	data: FAQData;
}

export default function FAQSection({ data }: FAQSectionProps) {
	const [activeSection, setActiveSection] = useState<string>("");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const displayNav = data.sections?.length > 1;
	const t = useContext<Dictionary>(DictionaryContext);

	// Handle scroll to section
	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId);
		if (element) {
			const offset = 100; // Account for fixed header
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition = elementPosition + window.pageYOffset - offset;

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			});
			setActiveSection(sectionId);
		}
	};

	// Update active section based on intersection observer
	useEffect(() => {
		const observerOptions = {
			root: null,
			rootMargin: "-20% 0px -80% 0px", // Only trigger when section is in the top 20% of viewport
			threshold: 0,
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setActiveSection(entry.target.id);
				}
			});
		}, observerOptions);

		// Observe all sections
		data.sections.forEach((section) => {
			const element = document.getElementById(section.id);
			if (element) {
				observer.observe(element);
			}
		});

		return () => {
			observer.disconnect();
		};
	}, [data.sections]);

	// Filter data based on search term
	const filteredData = useMemo(() => {
		if (!searchTerm.trim()) return data;

		const filtered = {
			sections: data.sections
				.map((section) => ({
					...section,
					subsections: section.subsections
						.map((subsection) => ({
							...subsection,
							faqs: subsection.faqs.filter(
								(faq) =>
									faq.question
										.toLowerCase()
										.includes(searchTerm.toLowerCase()) ||
									faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
							),
						}))
						.filter((subsection) => subsection.faqs.length > 0),
				}))
				.filter((section) => section.subsections.length > 0),
		};

		return filtered;
	}, [data, searchTerm]);

	// Format answer text with bold, proper line breaks, and email links
	const formatAnswer = (answer: string) => {
		return answer
			.split("**")
			.map((part, index) =>
				index % 2 === 1 ? `<strong>${part}</strong>` : part
			)
			.join("")
			.replace(/\n/g, "<br />")
			.replace(
				/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
				'<a href="mailto:$1" class="text-primary hover:underline">$1</a>'
			);
	};

	return (
		<div id="faqs">
			{/* className="min-h-screen" commented out to remove space */}
			{/* Title and Description */}
			<div className="max-w-7xl mx-auto px-4 pb-8">
				<div className="mb-10 animate-fadeIn">
					<div className="text-2xl md:text-3xl font-bold text-black mb-4">
						{t.faq.title}
					</div>
					<p className="text-lg text-black/80 w-full mb-8">
						{t.faq.description}
					</p>
				</div>

				{/* Search Bar - Centered */}
				<div className="max-w-sm mx-auto mb-4">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
						<Input
							type="text"
							placeholder={t.faq.search}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-10 bg-white"
						/>
					</div>
				</div>
			</div>

			{/* Mobile Navigation - Flex */}
			<div className="lg:hidden mb-8">
				<div className="max-w-7xl mx-auto px-4">
					<div className="flex flex-wrap gap-2 justify-start">
						{filteredData.sections.map((section) => (
							<Button
								key={section.id}
								variant={activeSection === section.id ? "default" : "outline-neutral"}
								size="sm"
								onClick={() => scrollToSection(section.id)}
								className="hover:bg-primary hover:text-primary-foreground"
							>
								{section.title}
							</Button>
						))}
					</div>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 pb-12">
				<div className="flex gap-8">
					{displayNav && (
						<>
							{/* Desktop Sidebar Navigation */}
							<div className="hidden lg:block w-80 flex-shrink-0">
								<div className="sticky top-8">
									<div className="p-4">
										<h3 className="font-semibold text-lg">{t?.faq?.categories}</h3>
										<div className="w-full h-0.5 bg-primary mb-6"></div>
										<nav className="space-y-0">
											{filteredData.sections.map((section) => (
												<Button
													key={section.id}
													variant="ghost"
													className={`w-full justify-start text-left hover:bg-primary hover:text-primary-foreground whitespace-normal h-auto py-3 px-3 cursor-pointer text-sm ${
														activeSection === section.id
															? "text-primary font-medium"
															: "text-foreground"
													}`}
													onClick={() => scrollToSection(section.id)}
													dangerouslySetInnerHTML={{
														__html: formatAnswer(section.title),
													}}
												/>
											))}
										</nav>
									</div>
								</div>
							</div>
						</>
					)}

					{/* Main Content */}
					<div className={classNames("flex-1", {"max-w-4xl": displayNav})}>
						{filteredData.sections.length === 0 && searchTerm ? (
							<div className="text-center py-12">
								<p className="text-lg text-muted-foreground mb-4">
									{t.faq.not_found} &#34;{searchTerm}&#34;
								</p>
								<Button variant="outline-neutral" onClick={() => setSearchTerm("")}>
									{t.faq.clear_search}
								</Button>
							</div>
						) : (
							filteredData.sections.map((section) => (
								<section key={section.id} id={section.id} className="mb-16">
									{displayNav && (section?.title?.length > 0) && (
										<>
											{/* Section Title */}
											<h3 className="text-xl md:text-2xl lg:text-3xl font-normal text-foreground mb-8 pb-4 border-b-2 border-primary"
												dangerouslySetInnerHTML={{
													__html: formatAnswer(section.title),
												}}
											/>
										</>
									)}

									{/* Subsections */}
									{section.subsections.map((subsection) => (
										<div key={subsection.id} className="mb-9">
											{/* Subsection Title */}
											{(subsection?.title?.length >0) && (
												<h4 className="text-base lg:text-xl font-semibold text-foreground mb-4 pb-0"
													dangerouslySetInnerHTML={{
														__html: formatAnswer(subsection.title),
													}}
												/>
											)}

											{/* FAQs */}
											<Accordion
												type="single"
												collapsible
												className="space-y-3"
											>
												{subsection.faqs.map((faq, index) => (
													<AccordionItem
														key={index}
														value={`${subsection.id}-${index}`}
														className="border border-border rounded-lg bg-white shadow-sm [&>h3]:p-0"
													>
														<AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-muted-light data-[state=open]:bg-muted-light hover:data-[state=open]:bg-muted cursor-pointer rounded-t-lg">
															<span className="font-medium text-foreground pr-4 text-sm"
																		dangerouslySetInnerHTML={{
																			__html: formatAnswer(faq.question),
																		}}
															/>
														</AccordionTrigger>
														<AccordionContent className="px-6 pb-2 pt-2 [&_p]:prose [&_p]:prose-sm">
															<div
																className="faq-item text-foreground/80 leading-relaxed prose prose-sm max-w-none [&_p]:max-w-none [&>p]:mb-4 [&_p]:font-normal"
																dangerouslySetInnerHTML={{
																	__html: formatAnswer(faq.answer),
																}}
															/>
														</AccordionContent>
													</AccordionItem>
												))}
											</Accordion>
										</div>
									))}
								</section>
							))
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
